import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Search, RefreshCw, ChevronRight, Box, History, Clock, Truck, ChevronDown, ChevronUp, Warehouse, CalendarCheck, PackageX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import SeasonalBoxesTab from './SeasonalBoxesTab';
import CompletedByDateTab from './CompletedByDateTab';
import PackedNotSentTab from './PackedNotSentTab';

const API = process.env.REACT_APP_BACKEND_URL;

const PackingDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('slips');
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [packingStats, setPackingStats] = useState({ pending_qty: 0, slips_with_pending: 0 });

  const fetchSlips = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/wms/packing/completed-slips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch slips');
      
      const data = await res.json();
      setSlips(data.slips || []);
    } catch (error) {
      console.error('Error fetching slips:', error);
      toast.error('Failed to load pick slips');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPackingStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/wms/packing/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPackingStats(data);
      }
    } catch (error) {
      console.error('Error fetching packing stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchSlips();
    fetchPackingStats();
  }, [fetchSlips, fetchPackingStats]);

  const filteredSlips = slips.filter(slip => {
    const term = searchTerm.toLowerCase();
    return (
      slip.pick_slip_number?.toLowerCase().includes(term) ||
      slip.customer?.toLowerCase().includes(term) ||
      slip.agent?.toLowerCase().includes(term) ||
      slip.transport_name?.toLowerCase().includes(term)
    );
  });

  // Group slips by transport
  const groupedByTransport = useMemo(() => {
    const groups = {};
    filteredSlips.forEach(slip => {
      const transportKey = slip.transport_name || 'No Transport Assigned';
      if (!groups[transportKey]) {
        groups[transportKey] = {
          transport_name: slip.transport_name,
          transport_id: slip.transport_id,
          slips: [],
          total_remaining: 0
        };
      }
      groups[transportKey].slips.push(slip);
      groups[transportKey].total_remaining += slip.remaining_to_pack || 0;
    });
    
    // Sort groups: assigned transports first, then "No Transport Assigned"
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'No Transport Assigned') return 1;
      if (b === 'No Transport Assigned') return -1;
      return a.localeCompare(b);
    });
    
    return sortedKeys.map(key => groups[key]);
  }, [filteredSlips]);

  // Track expanded transport groups
  const [expandedGroups, setExpandedGroups] = useState({});

  // Initialize all groups as expanded
  useEffect(() => {
    const initialExpanded = {};
    groupedByTransport.forEach(group => {
      const key = group.transport_name || 'No Transport Assigned';
      initialExpanded[key] = true;
    });
    setExpandedGroups(initialExpanded);
  }, [groupedByTransport.length]);

  const toggleGroup = (transportName) => {
    const key = transportName || 'No Transport Assigned';
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PACKED':
        return 'bg-green-100 text-green-800';
      case 'PARTIALLY_PACKED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="packing-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Box className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">Packing Station</h1>
                <p className="text-sm text-[#E8DCC8]">Pack picked items into boxes</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/wms/pick-pack-live')}
              variant="outline"
              className="border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
              data-testid="packing-pick-pack-live-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Pick-Pack Live
            </Button>
            <Button
              onClick={() => navigate('/wms/fg/bundle-history')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="packing-bundle-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Bundle History
            </Button>
            <Button
              onClick={fetchSlips}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="packing-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setTab('slips')}
            variant={tab === 'slips' ? 'default' : 'outline'}
            className={tab === 'slips' ? 'bg-[#2D5F4F] hover:bg-[#244C40]' : 'border-[#E5DFD0]'}
            data-testid="packing-tab-slips"
          >
            <Package className="w-4 h-4 mr-2" /> Pick Slips
          </Button>
          <Button
            onClick={() => setTab('seasonal')}
            variant={tab === 'seasonal' ? 'default' : 'outline'}
            className={tab === 'seasonal' ? 'bg-[#2D5F4F] hover:bg-[#244C40]' : 'border-[#E5DFD0]'}
            data-testid="packing-tab-seasonal"
          >
            <Warehouse className="w-4 h-4 mr-2" /> Seasonal Boxes
          </Button>
          <Button
            onClick={() => setTab('completed-by-date')}
            variant={tab === 'completed-by-date' ? 'default' : 'outline'}
            className={tab === 'completed-by-date' ? 'bg-[#2D5F4F] hover:bg-[#244C40]' : 'border-[#E5DFD0]'}
            data-testid="packing-tab-completed-by-date"
          >
            <CalendarCheck className="w-4 h-4 mr-2" /> Completed by Date
          </Button>
          <Button
            onClick={() => setTab('packed-not-sent')}
            variant={tab === 'packed-not-sent' ? 'default' : 'outline'}
            className={tab === 'packed-not-sent' ? 'bg-[#2D5F4F] hover:bg-[#244C40]' : 'border-[#E5DFD0]'}
            data-testid="packing-tab-packed-not-sent"
          >
            <PackageX className="w-4 h-4 mr-2" /> Packed not Sent
          </Button>
        </div>

        {tab === 'seasonal' ? (
          <SeasonalBoxesTab />
        ) : tab === 'completed-by-date' ? (
          <CompletedByDateTab />
        ) : tab === 'packed-not-sent' ? (
          <PackedNotSentTab />
        ) : (
        <>
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by slip number, customer, or agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-[#E5DFD0]"
              data-testid="packing-search-input"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Pending Qty to Pack</p>
                <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="pending-qty-stat">
                  {packingStats.pending_qty?.toLocaleString() || 0}
                </p>
              </div>
              <Clock className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Ready to Pack</p>
                <p className="text-2xl font-bold text-[#1A1F2E]">
                  {slips.filter(s => s.packing_status === 'READY_TO_PACK').length}
                </p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Partially Packed</p>
                <p className="text-2xl font-bold text-[#1A1F2E]">
                  {slips.filter(s => s.packing_status === 'PARTIALLY_PACKED').length}
                </p>
              </div>
              <Box className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Total Slips</p>
                <p className="text-2xl font-bold text-[#1A1F2E]">{slips.length}</p>
              </div>
              <Package className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Slips List - Grouped by Transport */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0] flex items-center justify-between">
            <h2 className="font-semibold text-[#1A1F2E]">
              Completed Pick Slips ({filteredSlips.length})
            </h2>
            <span className="text-sm text-[#5A5F6E]">
              {groupedByTransport.length} transport group(s)
            </span>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#2D5F4F]" />
              <p className="mt-2 text-[#5A5F6E]">Loading pick slips...</p>
            </div>
          ) : filteredSlips.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-2 text-[#5A5F6E]">
                {searchTerm ? 'No matching slips found' : 'No completed pick slips ready for packing'}
              </p>
            </div>
          ) : (
            <div>
              {groupedByTransport.map((group) => {
                const groupKey = group.transport_name || 'No Transport Assigned';
                const isExpanded = expandedGroups[groupKey] !== false;
                
                return (
                  <div key={groupKey} className="border-b border-[#E5DFD0] last:border-b-0">
                    {/* Transport Group Header */}
                    <div 
                      className="px-4 py-3 bg-purple-50 flex items-center justify-between cursor-pointer hover:bg-purple-100 transition-colors"
                      onClick={() => toggleGroup(group.transport_name)}
                      data-testid={`transport-group-${groupKey}`}
                    >
                      <div className="flex items-center gap-3">
                        <Truck className="w-5 h-5 text-purple-600" />
                        <div>
                          <span className="font-semibold text-purple-800">
                            {groupKey}
                          </span>
                          <span className="ml-3 text-sm text-purple-600">
                            {group.slips.length} slip(s)
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-purple-700 font-medium">
                          Remaining: {group.total_remaining.toLocaleString()}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-purple-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                    </div>
                    
                    {/* Slips in this Transport Group */}
                    {isExpanded && (
                      <div className="divide-y divide-[#E5DFD0]">
                        {group.slips.map((slip) => (
                          <div
                            key={slip.pick_slip_number}
                            onClick={() => navigate(`/wms/fg/packing/${slip.pick_slip_number}`)}
                            className="px-4 py-4 hover:bg-[#FFFBF5] cursor-pointer transition-colors flex items-center justify-between pl-12"
                            data-testid={`packing-slip-${slip.pick_slip_number}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <span className="font-semibold text-[#1A1F2E]">
                                  {slip.pick_slip_number}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(slip.packing_status)}`}>
                                  {slip.packing_status?.replace('_', ' ')}
                                </span>
                                {slip.is_seasonal && (
                                  <span
                                    data-testid={`packing-seasonal-badge-${slip.pick_slip_number}`}
                                    className="px-2 py-0.5 rounded-full text-xs font-semibold bg-lime-100 text-lime-800 border border-lime-300"
                                    title="Seasonal slip — no DC required for packing"
                                  >
                                    Seasonal
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-[#5A5F6E]">
                                <span>{slip.customer}</span>
                                {slip.agent && <span className="ml-3">Agent: {slip.agent}</span>}
                              </div>
                              <div className="mt-1 text-sm text-[#5A5F6E] flex flex-wrap gap-x-4 gap-y-1">
                                {slip.dc_number && (
                                  <span className="text-blue-600 font-medium">DC: {slip.dc_number}</span>
                                )}
                              </div>
                              <div className="mt-1 text-xs text-[#5A5F6E]">
                                Picked: {slip.total_picked} | Packed: {slip.total_packed} | 
                                <span className="text-[#2D5F4F] font-medium ml-1">
                                  Remaining: {slip.remaining_to_pack}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
};

export default PackingDashboard;
